import React, { useCallback, useEffect, useState, useContext } from "react";
import { useTranslation, Trans } from "react-i18next";

import { proceedNextSetp } from "./keylessService";
import { DomainContext } from "../../domain-repository/DomainRepository";
import { errorModalImgStyle, keylessColors } from "./utils";

import AlertModalManager from "../../components/alert-modal-wraper/alert-modal-manager";
import { KeylessButton } from "./components/KeylessButton";
import { KeylessModal } from "./components/KeylessModal";
import {
  KEYLESS_STEPS,
  KeylessPageLayout,
} from "./components/KeylessPageLayout";

const STEPS = {
  INIT: "INIT",
  PROCEED_PARKING_LOCATION: "PROCEED_PARKING_LOCATION",
  PARKING_LOCATION_QUESTION: "PARKING_LOCATION_QUESTION",
}

export function KeylessValidateParkingLocation({
  handleNextStep,
  keylessToken,
  keylessSession,
}) {
  const { t } = useTranslation();

  const { localizationAssets } = useContext(DomainContext);

  const [isLoading, setIsLoading] = useState(false);
  const [openLeaveModal, setOpenLeaveModal] = useState(false);

  const handleConfirmClick = useCallback(() => {
    async function func() {
      setIsLoading(true);
      proceedNextSetp(keylessToken)
        .then((res) => {
          handleNextStep(res.nextStep);
        })
        .catch((err) => {
          const messageKey = err.response.data;
          AlertModalManager.openModal(
            { title: t(messageKey), message: "" },
            () => {}
          );
        })
        .finally(() => setIsLoading(false));
    }
    func();
  }, [keylessToken, handleNextStep, t]);

  const handleLeaveClick = useCallback(() => {
    window.location.href = "/";
  }, []);

  const [currentView, setCurrentView] = useState(STEPS.INIT);
  useEffect(() => {
    const t = setTimeout(
      () => setCurrentView(STEPS.PARKING_LOCATION_QUESTION),
      4000
    );
    return () => clearTimeout(t);
  }, []);

  if (currentView === STEPS.INIT) {
    return (
      <InitView
        videoSrc={localizationAssets["keylessInit"]}
        logoSrc={localizationAssets["keylessGuerinLogo"]}
        text={t("keylessInitMessage")}
      />
    );
  }

  if (currentView === STEPS.PROCEED_PARKING_LOCATION) {
    return (
      <KeylessPageLayout
        t={t}
        step={KEYLESS_STEPS.LOCATION}
        localizationAssets={localizationAssets}
        reservationNumber={keylessSession.reservationNumber}
        footer={
          <KeylessButton
            onClick={handleConfirmClick}
            text={t("keylessNext")}
            color="white"
            backgroundColor={keylessColors.orange}
          />
        }
      >
        <div style={styles.pageContent} className="container">
          <h1 style={styles.parkingTitle}>{t("keylessVehicleParkingTitle")}</h1>
          <img
            style={styles.parkingImage}
            src={localizationAssets["keylessParkingImage"]}
            alt=""
          />
          {keylessSession.vehicleParkingLocation ? 
            <div style={styles.parkingLocation}>
              <div>{t("keylessVehicleParkingLabel")}</div>
              <div style={styles.parkingLocationValue}>
                {keylessSession.vehicleParkingLocation}
              </div>
            </div>
          : null}
        </div>
      </KeylessPageLayout>
    );
  }

  return (
    <div style={styles.page}>
      <div className="container" style={styles.pageContent}>
        <img
          src={localizationAssets["keylessGuerinLogo"]}
          alt="Guerin"
          style={styles.logo}
        />
        <img
          src={localizationAssets["keylessStationImage"]}
          alt=""
          style={styles.stationImage}
        />
        <p style={styles.title}>
          <Trans
            i18nKey={
              keylessSession.vehicleParkingLocation
                ? "keylessParkingLocationValidate"
                : "keylessWithoutParkingLocationValidate"
            }
            values={{
              driverName: keylessSession.driverName,
              parkingLocation: keylessSession.vehicleParkingLocation,
            }}
            components={[<br />]}
          />
        </p>
        <div className="grid gap-4">
          <KeylessButton
            onClick={handleConfirmClick}
            text={t("yes")}
            color="white"
            backgroundColor={keylessColors.orange}
            isLoading={isLoading}
          />
          <KeylessButton
            onClick={() => setCurrentView("PROCEED_PARKING_LOCATION")}
            text={t("no")}
            color="white"
            backgroundColor="rgba(0, 0 , 0, .10)"
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
}

function InitView({ videoSrc, logoSrc, text }) {
  return (
    <div style={styles.initSection}>
      <video
        loop
        autoPlay
        muted
        src={videoSrc}
        style={styles.initVideo}
        alt=""
      />
      <img style={styles.initLogo} src={logoSrc} alt="" />
      <div className="fade-in" style={styles.initText}>
        {text}
      </div>
    </div>
  );
}

const styles = {
  initSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    height: "100dvh",
    padding: "10% 0 50%",
    background: "rgba(0, 0, 0, 0.4)",
  },
  initVideo: {
    height: "100dvh",
    position: "absolute",
    top: "0",
    left: "0",
    zIndex: "-1",
  },
  initLogo: {
    width: "40%",
    maxWidth: "200px",
  },
  initText: {
    color: "white",
    fontSize: "2.5rem",
    textAlign: "center",
    fontFamily: "'BlissLight'",
    width: "60%",
  },
  page: {
    backgroundColor: keylessColors.green,
    height: "100dvh",
    paddingBottom: "60px",
    paddingTop: "30px",
    fontFamily: "'BlissLight'",
  },
  pageContent: {
    textAlign: "center",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
  },
  logo: {
    height: "10vh",
    margin: "0 auto",
  },
  stationImage: {
    height: "20vh",
    margin: "0 auto",
  },
  title: {
    color: "white",
    fontSize: "2rem",
    flex: "1",
    margin: "0",
    lineHeight: "normal",
  },
  parkingTitle: {
    fontSize: "2rem",
    margin: "0",
    lineHeight: "normal",
  },
  parkingImage: {
    height: "20vh",
    margin: "0 auto",
    display: "block",
  },
  parkingLocation: {
    backgroundColor: keylessColors.green,
    borderRadius: "20px",
    padding: "5px",
    width: "70%",
    color: "white",
    fontSize: "20px",
    margin: "0 auto",
    textAlign: "center",
  },
  parkingLocationValue: {
    fontSize: "20px",
  },
};
